import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { of } from 'rxjs'
import {
  ActionFailed,
  ActionSkipped,
  loadRentalComplete,
  loadRentalsComplete,
  selectOnce,
  syncRentalEntityTags,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { EntityTagsService } from './service'
import {
  AddEntityTags,
  AddEntityTagsComplete,
  DeleteEntityTags,
  DeleteEntityTagsComplete,
  GetEntityTags,
  GetEntityTagsComplete,
  LoadEntityTags,
  LoadEntityTagsComplete,
  RemoveTagFromAllEntities,
  RemoveTagFromAllEntitiesComplete,
  SetEntityTags,
  UpdateEntityTags,
  UpdateEntityTagsComplete,
} from './actions'
import { select, Store } from '@ngrx/store'
import { EntityTagsResponse } from './model'
import { selectAllEntityTags, selectTagsEntity } from './selectors'
import { entityTagsToRentalUpdates, resolveSetEntityTagsOperation } from './tag-sync'

@Injectable()
export class EntityTagsEffects {
  @Effect()
  load$ = this.actions$.pipe(
    ofType(LoadEntityTags),
    switchMap(() =>
      this.tagsService.getAllTags().pipe(
        map((items) => LoadEntityTagsComplete({ items })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  get$ = this.actions$.pipe(
    ofType(GetEntityTags),
    switchMap(({ entityType, entityId }) =>
      this.tagsService.getTags(entityType, entityId).pipe(
        map((item) => GetEntityTagsComplete({ item })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  add$ = this.actions$.pipe(
    ofType(AddEntityTags),
    switchMap(({ entityType, entityId, tags, silent }) =>
      this.tagsService.addTags(entityType, entityId, tags).pipe(
        map((item) => AddEntityTagsComplete({ item })),
        tap(() => !silent && this.toaster.success($localize`Tags updated successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  update$ = this.actions$.pipe(
    ofType(UpdateEntityTags),
    switchMap(({ entityType, entityId, tags, silent }) =>
      this.tagsService.updateTags(entityType, entityId, tags).pipe(
        map((item) => UpdateEntityTagsComplete({ item })),
        tap(() => !silent && this.toaster.success($localize`Tags updated successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  set$ = this.actions$.pipe(
    ofType(SetEntityTags),
    switchMap((data) =>
      this.store.pipe(
        selectOnce(selectTagsEntity(data.entityType, data.entityId)),
        map((e: EntityTagsResponse | undefined) =>
          resolveSetEntityTagsOperation(data.tags, e) === 'skip' ? ActionSkipped() : UpdateEntityTags(data)
        )
      )
    )
  )

  @Effect()
  syncRentalTags$ = this.actions$.pipe(
    ofType(
      LoadEntityTagsComplete,
      GetEntityTagsComplete,
      AddEntityTagsComplete,
      UpdateEntityTagsComplete,
      loadRentalComplete,
      loadRentalsComplete
    ),
    withLatestFrom(this.store.pipe(select(selectAllEntityTags))),
    map(([, items]) => syncRentalEntityTags({ updates: entityTagsToRentalUpdates(items) }))
  )

  @Effect()
  delete$ = this.actions$.pipe(
    ofType(DeleteEntityTags),
    switchMap(({ id, silent }) =>
      this.tagsService.deleteTags(id).pipe(
        map(() => DeleteEntityTagsComplete({ id })),
        tap(() => !silent && this.toaster.success($localize`Tags updated successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  // TV4-6241: Remove tag from all entities of the given type, then refresh the store.
  @Effect()
  removeTagFromAllEntities$ = this.actions$.pipe(
    ofType(RemoveTagFromAllEntities),
    concatMap(({ entityType, tag, silent }) =>
      this.tagsService.removeTagFromAllEntities(entityType, tag).pipe(
        map(() => RemoveTagFromAllEntitiesComplete({ entityType, tag })),
        tap(() => !silent && this.toaster.success($localize`Tag removed from all entities`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  // After the bulk-remove completes, reload all tags so the store reflects the change.
  @Effect()
  removeTagFromAllEntitiesComplete$ = this.actions$.pipe(
    ofType(RemoveTagFromAllEntitiesComplete),
    map(() => LoadEntityTags())
  )

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private toaster: Toaster,
    private tagsService: EntityTagsService
  ) {}
}
